// src/components/AttendancePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { db } from '../firebase';
import {
    collection, query, where, getDocs, orderBy, deleteDoc, doc, addDoc, Timestamp
} from 'firebase/firestore';

// Import Font Awesome Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt, faSpinner, faChartLine, faCog, faUsers, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

// Import react-bootstrap components
import { Alert, Modal, Button, Form } from 'react-bootstrap';

// Import recharts
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Import the Auth Context hook
import { useAuth } from '../context/AuthContext';

import './AttendancePage.css';

// --- Constants ---
const RECORDS_PER_PAGE = 20;

// --- Helper Functions ---
const formatRecordDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

const formatChartDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    });
};

// --- Date Range Helpers ---
const getDateRange = (range) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    switch (range) {
        case 'thisMonth':
            return { start: startOfMonth, end: now };
        case 'lastMonth':
            return { start: startOfLastMonth, end: endOfLastMonth };
        case 'ytd':
            return { start: startOfYear, end: now };
        case 'all':
        default:
            return { start: null, end: null };
    }
};

// --- Main Component: AttendancePage ---
function AttendancePage() {
    const { currentUser, isAdmin, userOrgId, loadingAuth } = useAuth();

    // Local state
    const [records, setRecords] = useState([]);
    const [eventTypes, setEventTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter state
    const [selectedType, setSelectedType] = useState('all');
    const [dateRange, setDateRange] = useState('thisMonth');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Add modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [newRecord, setNewRecord] = useState({
        eventType: '',
        date: new Date().toISOString().split('T')[0],
        numberAttended: ''
    });
    const [isAdding, setIsAdding] = useState(false);

    // --- Fetch Event Types ---
    const fetchEventTypes = useCallback(async () => {
        if (!userOrgId) return [];

        try {
            const typesQuery = query(
                collection(db, 'attendanceType'),
                where('orgId', '==', userOrgId),
                orderBy('name', 'asc')
            );
            const typesSnap = await getDocs(typesQuery);
            return typesSnap.docs.map(d => ({
                ...d.data(),
                id: d.id
            }));
        } catch (err) {
            console.error("Error fetching event types:", err);
            return [];
        }
    }, [userOrgId]);

    // --- Fetch Attendance Records ---
    const fetchRecords = useCallback(async () => {
        if (!userOrgId) {
            if (!loadingAuth && currentUser) {
                setError("Organization details missing from your account.");
            }
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const [types, recordsSnap] = await Promise.all([
                fetchEventTypes(),
                getDocs(query(
                    collection(db, 'attendance'),
                    where('orgId', '==', userOrgId),
                    orderBy('date', 'desc')
                ))
            ]);

            setEventTypes(types);

            const fetchedRecords = recordsSnap.docs.map(d => ({
                ...d.data(),
                id: d.id
            }));

            setRecords(fetchedRecords);
            setError(null);
        } catch (err) {
            console.error("AttendancePage: Error fetching records", err);
            setError(err.message || "Failed to load attendance records.");
        } finally {
            setLoading(false);
        }
    }, [userOrgId, loadingAuth, currentUser, fetchEventTypes]);

    useEffect(() => {
        if (!loadingAuth && userOrgId) {
            fetchRecords();
        } else if (!loadingAuth && !userOrgId && currentUser) {
            setError("Organization details missing from your account.");
            setLoading(false);
        } else if (!loadingAuth && !currentUser) {
            setRecords([]);
            setLoading(false);
            setError(null);
        }
    }, [loadingAuth, userOrgId, fetchRecords, currentUser]);

    // --- Filter records ---
    const filteredRecords = records.filter(record => {
        // Type filter
        if (selectedType !== 'all' && record.eventType !== selectedType) {
            return false;
        }

        // Date range filter
        const { start, end } = getDateRange(dateRange);
        if (start && end) {
            const recordDate = record.date instanceof Timestamp
                ? record.date.toDate()
                : new Date(record.date);
            if (recordDate < start || recordDate > end) {
                return false;
            }
        }

        return true;
    });

    // --- Pagination calculations ---
    const totalPages = Math.ceil(filteredRecords.length / RECORDS_PER_PAGE);
    const startIndex = (currentPage - 1) * RECORDS_PER_PAGE;
    const endIndex = startIndex + RECORDS_PER_PAGE;
    const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

    // Reset to page 1 when filters change
    const handleTypeFilter = (type) => {
        setSelectedType(type);
        setCurrentPage(1);
    };

    const handleDateRangeFilter = (range) => {
        setDateRange(range);
        setCurrentPage(1);
    };

    // --- Chart data ---
    const chartData = filteredRecords
        .slice()
        .sort((a, b) => {
            const dateA = a.date instanceof Timestamp ? a.date.toDate() : new Date(a.date);
            const dateB = b.date instanceof Timestamp ? b.date.toDate() : new Date(b.date);
            return dateA - dateB;
        })
        .map(record => ({
            date: formatChartDate(record.date),
            attendance: record.numberAttended,
            type: record.eventType
        }));

    // --- Delete Handler ---
    const handleDeleteClick = (record) => {
        setRecordToDelete(record);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!recordToDelete || !userOrgId || !isAdmin) return;

        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'attendance', recordToDelete.id));
            setRecords(prev => prev.filter(r => r.id !== recordToDelete.id));
            setShowDeleteModal(false);
            setRecordToDelete(null);
        } catch (err) {
            console.error("Error deleting record:", err);
            setError("Failed to delete record: " + err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCloseDeleteModal = () => {
        if (!isDeleting) {
            setShowDeleteModal(false);
            setRecordToDelete(null);
        }
    };

    // --- Add Handler ---
    const handleOpenAddModal = () => {
        setNewRecord({
            eventType: eventTypes.length > 0 ? eventTypes[0].name : '',
            date: new Date().toISOString().split('T')[0],
            numberAttended: ''
        });
        setShowAddModal(true);
    };

    const handleAddRecord = async () => {
        if (!newRecord.eventType || !newRecord.date || !newRecord.numberAttended) {
            setError("Please fill in all fields.");
            return;
        }

        setIsAdding(true);
        try {
            const docRef = await addDoc(collection(db, 'attendance'), {
                orgId: userOrgId,
                eventType: newRecord.eventType,
                date: Timestamp.fromDate(new Date(newRecord.date)),
                numberAttended: parseInt(newRecord.numberAttended, 10),
                recordedBy: currentUser.uid,
                createdAt: Timestamp.now()
            });

            const addedRecord = {
                id: docRef.id,
                orgId: userOrgId,
                eventType: newRecord.eventType,
                date: Timestamp.fromDate(new Date(newRecord.date)),
                numberAttended: parseInt(newRecord.numberAttended, 10),
                recordedBy: currentUser.uid,
                createdAt: Timestamp.now()
            };

            setRecords(prev => [addedRecord, ...prev]);
            setShowAddModal(false);
            setNewRecord({
                eventType: '',
                date: new Date().toISOString().split('T')[0],
                numberAttended: ''
            });
        } catch (err) {
            console.error("Error adding record:", err);
            setError("Failed to add record: " + err.message);
        } finally {
            setIsAdding(false);
        }
    };

    const handleCloseAddModal = () => {
        if (!isAdding) {
            setShowAddModal(false);
        }
    };

    // --- Render Logic ---

    // Loading state
    if (loadingAuth || loading) {
        return (
            <div className="attendance-page">
                <div className="attendance-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-filters"></div>
                    <div className="skeleton-chart"></div>
                    <div className="skeleton-record"></div>
                    <div className="skeleton-record"></div>
                </div>
            </div>
        );
    }

    // Not logged in
    if (!currentUser) {
        return (
            <div className="attendance-page">
                <div className="container mt-3">
                    <Alert variant="warning">
                        Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view attendance.
                    </Alert>
                </div>
            </div>
        );
    }

    // Not admin
    if (!isAdmin) {
        return (
            <div className="attendance-page">
                <div className="attendance-header">
                    <div className="container">
                        <div className="attendance-header-content">
                            <div>
                                <h1 className="attendance-title">Attendance <span className="admin-only-badge">Admin Only</span></h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="attendance-admin-only">
                        <div className="attendance-admin-only-icon">
                            <FontAwesomeIcon icon={faChartLine} />
                        </div>
                        <h3>Admin Access Required</h3>
                        <p>Attendance tracking is only available to organization administrators.</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="attendance-page">
                <div className="attendance-header">
                    <div className="container">
                        <div className="attendance-header-content">
                            <div>
                                <h1 className="attendance-title">Attendance <span className="admin-only-badge">Admin Only</span></h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        Error: {error}
                    </Alert>
                </div>
            </div>
        );
    }

    return (
        <div className="attendance-page">
            {/* Page Header */}
            <div className="attendance-header">
                <div className="container">
                    <div className="attendance-header-content">
                        <div>
                            <h1 className="attendance-title">Attendance <span className="admin-only-badge">Admin Only</span></h1>
                            {filteredRecords.length > 0 && (
                                <div className="attendance-count">
                                    {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''}
                                </div>
                            )}
                        </div>
                        <Link to="/attendance/types" className="attendance-types-btn">
                            <FontAwesomeIcon icon={faCog} />
                            <span>Manage Types</span>
                        </Link>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Event Type Filter */}
                <div className="attendance-filters">
                    <div className="attendance-filter-section">
                        <div className="attendance-filter-label">Event Type</div>
                        <div className="attendance-filter-chips">
                            <button
                                className={`filter-chip ${selectedType === 'all' ? 'active' : ''}`}
                                onClick={() => handleTypeFilter('all')}
                            >
                                All Types
                            </button>
                            {eventTypes.map(type => (
                                <button
                                    key={type.id}
                                    className={`filter-chip ${selectedType === type.name ? 'active' : ''}`}
                                    onClick={() => handleTypeFilter(type.name)}
                                >
                                    {type.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="attendance-filter-section">
                        <div className="attendance-filter-label">Date Range</div>
                        <div className="attendance-filter-chips">
                            <button
                                className={`filter-chip ${dateRange === 'thisMonth' ? 'active' : ''}`}
                                onClick={() => handleDateRangeFilter('thisMonth')}
                            >
                                This Month
                            </button>
                            <button
                                className={`filter-chip ${dateRange === 'lastMonth' ? 'active' : ''}`}
                                onClick={() => handleDateRangeFilter('lastMonth')}
                            >
                                Last Month
                            </button>
                            <button
                                className={`filter-chip ${dateRange === 'ytd' ? 'active' : ''}`}
                                onClick={() => handleDateRangeFilter('ytd')}
                            >
                                YTD
                            </button>
                            <button
                                className={`filter-chip ${dateRange === 'all' ? 'active' : ''}`}
                                onClick={() => handleDateRangeFilter('all')}
                            >
                                All Time
                            </button>
                        </div>
                    </div>
                </div>

                {/* Trend Chart */}
                {chartData.length > 0 && (
                    <div className="attendance-chart-container">
                        <h3 className="attendance-section-title">Attendance Trend</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                                <YAxis stroke="#64748b" fontSize={12} />
                                <Tooltip
                                    contentStyle={{
                                        background: 'white',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="attendance"
                                    stroke="#3b82f6"
                                    strokeWidth={2}
                                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                                    activeDot={{ r: 6, stroke: '#1e3a5f', strokeWidth: 2 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* Records Section */}
                <div className="attendance-records-section">
                    <h3 className="attendance-section-title">
                        Records
                        {filteredRecords.length > 0 && (
                            <span className="records-count">
                                ({startIndex + 1}-{Math.min(endIndex, filteredRecords.length)} of {filteredRecords.length})
                            </span>
                        )}
                    </h3>

                    {/* Empty State */}
                    {filteredRecords.length === 0 && (
                        <div className="attendance-empty">
                            <div className="attendance-empty-icon">
                                <FontAwesomeIcon icon={faUsers} />
                            </div>
                            <h3>No Attendance Records</h3>
                            <p>
                                {eventTypes.length === 0
                                    ? "Start by adding event types, then record your first attendance."
                                    : "Record your first attendance to start tracking trends."
                                }
                            </p>
                            {eventTypes.length === 0 && (
                                <Link to="/attendance/types" className="btn btn-outline-primary mt-2">
                                    Add Event Types
                                </Link>
                            )}
                        </div>
                    )}

                    {/* Record Cards */}
                    {paginatedRecords.map(record => (
                        <div key={record.id} className="attendance-record-card">
                            <div className="record-avatar">
                                {record.numberAttended}
                            </div>
                            <div className="record-content">
                                <div className="record-type">{record.eventType}</div>
                                <div className="record-date">{formatRecordDate(record.date)}</div>
                            </div>
                            <div className="record-actions">
                                <button
                                    className="record-action-btn record-action-btn-delete"
                                    onClick={() => handleDeleteClick(record)}
                                    title="Delete Record"
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                            </div>
                        </div>
                    ))}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="attendance-pagination">
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                                <span>Previous</span>
                            </button>
                            <div className="pagination-info">
                                Page {currentPage} of {totalPages}
                            </div>
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                <span>Next</span>
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* FAB Button */}
            {eventTypes.length > 0 && (
                <button className="fab-add-attendance" onClick={handleOpenAddModal}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Add Attendance</span>
                </button>
            )}

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered className="attendance-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Delete Record</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this attendance record?</p>
                    {recordToDelete && (
                        <p className="text-muted">
                            <strong>{recordToDelete.eventType}</strong> - {recordToDelete.numberAttended} attended on {formatRecordDate(recordToDelete.date)}
                        </p>
                    )}
                    <p className="text-muted mb-0">This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
                        {isDeleting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            "Delete Record"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Attendance Modal */}
            <Modal show={showAddModal} onHide={handleCloseAddModal} centered className="attendance-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Add Attendance</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Event Type</Form.Label>
                            <Form.Select
                                value={newRecord.eventType}
                                onChange={(e) => setNewRecord({ ...newRecord, eventType: e.target.value })}
                            >
                                {eventTypes.map(type => (
                                    <option key={type.id} value={type.name}>{type.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={newRecord.date}
                                onChange={(e) => setNewRecord({ ...newRecord, date: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Number Attended</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                placeholder="Enter attendance count"
                                value={newRecord.numberAttended}
                                onChange={(e) => setNewRecord({ ...newRecord, numberAttended: e.target.value })}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseAddModal} disabled={isAdding}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleAddRecord} disabled={isAdding}>
                        {isAdding ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Adding...
                            </>
                        ) : (
                            "Add Record"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default AttendancePage;
